// src/components/AlertBanner.js
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_URLS } from "../config";
//import { useBackendHealth } from "../hooks/useBackendHealth";
//import { useBackendHealth } from "../hooks/useBackendHealth"; // ✅ fixed import path

// ── Fallback status (used when API is offline) ────────────────────────────────
const FALLBACK_STATUS = {
  geomagnetic:   { level: 0, label: "Quiet",  severity: 'safe'    },
  ionospheric:   { level: 1, label: "Active", severity: 'warning' },
  radioBlackout: { level: 0, label: "None",   severity: 'safe'    },
  solarFlux:     "—",
  kpIndex:       "—",
  updatedAt:     null,  // null = not yet loaded
  fromCache:     true,
};

const SEVERITY_BG = {
  safe:    'rgba(72,187,120,0.15)',
  warning: 'rgba(237,137,54,0.18)',
  caution: 'rgba(212,160,23,0.18)',
  danger:  'rgba(245,101,101,0.18)',
};
const SEVERITY_COLOR = {
  safe:    'var(--status-safe)',
  warning: 'var(--status-warning)',
  caution: 'var(--status-caution)',
  danger:  'var(--status-danger)',
};

/****
function relativeTime(ts) {
  if (!ts) return null;
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 60_000);
  if (diff < 1)  return 'Updated just now';
  if (diff < 60) return `Updated ${diff}m ago`;
  const hrs = Math.floor(diff / 60);
  return `Updated; ${hrs}h ago`;
}
*/

function relativeTime(ts) {
  if (!ts) return null;
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 60_000);
  if (diff < 1)  return 'Updated just now';
  if (diff < 60) return `Updated ${diff}m ago`;
  return 'Updated';
}

const BASE_NEWS = [
  "Ionospheric scintillation activity elevated over East Africa — aviation & GNSS users advised to check products",
  "TEC prediction model deployed covering 20°S–20°N, 25°E–50°E · Busitema Stacked Ensemble (XGBoost + LSTM + ANN)",
  "Ground station at Busitema University online — real-time GNSS data now available",
  "Research collaboration with SANSA — new scintillation datasets available on the Data Portal",
  "New publication: 'Stacked ensemble ML model for TEC prediction over Uganda' — see Publications",
];

export default function AlertBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [status, setStatus]       = useState(FALLBACK_STATUS);

  // Fetch live status from ML backend every 5 minutes
  useEffect(() => {
    let alive = true;

    async function fetchStatus() {
      try {
        const res = await fetch(API_URLS.spaceWeatherNow, {
          signal: AbortSignal.timeout(6_000),
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (!alive) return;
        setStatus({
          geomagnetic: {
            level:    data.kp_index >= 5 ? Math.floor(data.kp_index / 2) : 0,
            label:    data.kp_index >= 5 ? `G${Math.floor(data.kp_index / 2)}` : 'Quiet',
            severity: data.kp_index >= 7 ? 'danger' : data.kp_index >= 5 ? 'warning' : 'safe',
          },
          ionospheric: {
            level:    data.s4_index > 0.6 ? 2 : data.s4_index > 0.3 ? 1 : 0,
            label:    data.s4_index > 0.6 ? 'Strong' : data.s4_index > 0.3 ? 'moderate' : 'Quiet',
            severity: data.s4_index > 0.6 ? 'danger' : data.s4_index > 0.3 ? 'warning' : 'safe',
          },
          radioBlackout: { level: 0, label: 'None', severity: 'safe' },
          solarFlux: data.f107 != null ? `${Math.round(data.f107)} sfu` : '—',
          kpIndex:   data.kp_index != null ? data.kp_index.toFixed(1) : '—',
          updatedAt: data.timestamp ?? new Date().toISOString(),
          fromCache: false,
        });
      } catch {
        // API offline — keep fallback but set timestamp to show staleness
        if (alive) setStatus((s) => ({ ...s, fromCache: true }));
      }
    }

    fetchStatus();
    const id = setInterval(fetchStatus, 5 * 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (dismissed) return null;

  const hasAlert  = status.geomagnetic.level > 0 || status.ionospheric.level > 0;
  const timestamp = status.updatedAt
    ? relativeTime(status.updatedAt)
    : status.fromCache ? 'Status unavailable — backend offline' : null;

  const news = [
    ...BASE_NEWS,
    `Kp-index: ${status.kpIndex} · Solar Flux (F10.7): ${status.solarFlux}`,
  ];

  return (
    <div
      className="alert-banner relative z-50 text-sm"
      style={{
        background:   'var(--bg-void)',
        borderBottom: hasAlert
          ? '2px solid var(--status-warning)'
          : '2px solid var(--status-safe)',
      }}
    >
      {/* ── Status indicators row ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 border-b border-white/5">
        <div className="flex items-center gap-2 shrink-0">
          <span className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase"
            style={{ color: 'var(--text-muted)' }}>
            <span className="pulse-dot" />
            Live Status
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <AlertPill label="Geomagnetic Storm"         status={status.geomagnetic}   />
          <AlertPill label="Ionospheric Scintillation" status={status.ionospheric}   />
          <AlertPill label="Radio Blackout"            status={status.radioBlackout} />
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {timestamp && (
            <span
              className="text-xs hidden sm:block"
              style={{ color: status.fromCache ? 'var(--status-warning)' : 'var(--text-muted)' }}
              title={status.updatedAt ? new Date(status.updatedAt).toUTCString() : undefined}
            >
              {timestamp}
            </span>
          )}
          <Link
            to="/alerts"
            className="bu-btn bu-btn-secondary"
            style={{ fontSize: 'var(--text-2xs)', padding: '4px 10px', minHeight: '32px', textDecoration: 'none' }}
          >
            Full Alert Details →
          </Link>
          <button
            onClick={() => setDismissed(true)}
            className="bu-btn bu-btn-ghost"
            style={{ fontSize: 'var(--text-sm)', padding: '4px 8px', minHeight: '32px', minWidth: '32px' }}
            aria-label="Dismiss banner"
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Scrolling news ticker ─────────────────────────────────────── */}
      <div className="overflow-hidden py-1 px-3" aria-label="Latest space weather news" role="marquee">
        <div className="animate-ticker text-xs" style={{ color: 'var(--text-secondary)' }} aria-live="off">
          {news.join("   ·   ")}
        </div>
      </div>
    </div>
  );
}

function AlertPill({ label, status }) {
  const {label: statusLabel, severity } = status;
  const bg    = SEVERITY_BG[severity]    ?? SEVERITY_BG.safe;
  const color = SEVERITY_COLOR[severity] ?? SEVERITY_COLOR.safe;
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="hidden sm:inline" style={{ color: 'var(--text-muted)' }}>
        {label}:
      </span>
      <span
        className="px-2 py-0.5 rounded font-bold text-xs"
        style={{ background: bg, color, border: '1px solid currentColor' }}
      >
        {statusLabel}
      </span>
    </div>
  );
}
